import { faPen, faTable } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AlertTitle, Box, Card, CardContent, CircularProgress, Grid, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Theme, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { FC, Fragment, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { JSX } from "react/jsx-runtime";
import { GetSiteEventGetMarkerContent, Lookup, SiteEventType, Species, User } from "../../api/dtos/dtos";
import { SUCCESS_TIMEOUT } from "../../constants/styles/AlertConstants";
import AuthContext from "../../contexts/auth/AuthContext";
import MapContext, { MapFilterPeriodEnum, MapFilterSubmitter } from "../../contexts/data/MapContext";
import MapFormContext from "../../contexts/data/MapFormContext";
import { SpeciesContext } from "../../contexts/data/SpeciesContext";
import AlertContext from "../../contexts/styles/AlertContext";
import useGetSiteEventGetCollection from "../../hooks/data/useGetSiteEventGetCollection";
import useServiceStackClient from "../../hooks/api/useServiceStackClient";

interface RecordCardProps {
    title: string;
    siteEventType: SiteEventType;
    totalRecords: number
}

const RecordsCard: FC<RecordCardProps> = ({
    title,
    siteEventType,
    totalRecords
}) => {
    let navigate = useNavigate();
    const theme = useTheme();
    const client = useServiceStackClient();

    // 
    // -- States --
    //
    const [tableHeaders, setTableHeaders] = useState<string[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // 
    // -- Context
    const { user } = useContext(AuthContext);
    const { filter, setMapCenter } = useContext(MapContext);
    const { species: {species} } = useContext(SpeciesContext);
    const { addAlert } = useContext(AlertContext);
    const { setRecordToEdit, maximiseDrawer, openDrawer, setSiteEventType } = useContext(MapFormContext);

    //
    // -- Params
    const { species: speciesName } = useParams();

    const isAdminOrSuperAdmin = useMemo(() => {
        return !!user?.roles?.find(x => x === 'admin' || x === 'superadmin');
    }, [user]);

    const isDataAccessForThisSpecies = useMemo(() => {
        return user?.matchingRegionAdminRoles != undefined && user?.matchingRegionAdminRoles?.length > 0;
    }, [user]);

    // 
    // -- Generate headers on load
    useEffect(() => {
        generateTableHeaders(species);
    }, [species])

    const generateTableHeaders = (species: Species) => {
        let headers = [];

        headers.push("Date");

        // Start adding uncommon columns for: dog, deer, fox, cat, rabbit, myna, new pests
        if (species === Species.WildDog || species === Species.Deer || species === Species.Fox ||
            species === Species.FeralCat || species === Species.Rabbit || species === Species.Myna ||
            species === Species.NewPests || species === Species.FeralPig)
        {
            
            // Add columns that are special to dog, fox, deer, feralcat and feralpig only.
            if ((species === Species.WildDog || species === Species.Fox || species === Species.Deer || species === Species.FeralCat || species === Species.FeralPig) &&
                (siteEventType === SiteEventType.Sighting || siteEventType === SiteEventType.Damage || siteEventType === SiteEventType.Control)) {
                headers.push("Property Name");
            }

            // Add columns that are special to all except rabbit, myna, new pests: sighting type
            if (species !== Species.Rabbit && species !== Species.Myna && species !== Species.NewPests && siteEventType === SiteEventType.Sighting) {
                headers.push("Sighting Type");
            }

            // Site number: for cat - control only
            if (siteEventType === SiteEventType.Control && species === Species.FeralCat) {
                headers.push("Site Number");
            }
            // Site number: for myna - sighting and control only
            else if ((siteEventType === SiteEventType.Control || siteEventType === SiteEventType.Sighting) && species === Species.Myna) {
                headers.push("Site Number");
            }
            // Site number: for the rest - sighting, damage and control
            else if (siteEventType === SiteEventType.Sighting || siteEventType === SiteEventType.Damage || siteEventType === SiteEventType.Control) {
                headers.push("Site Number");
            }

            // Add the rest of the uncommon columns for all except myna and new pests.
            if (siteEventType === SiteEventType.Sighting && species !== Species.Myna && species !== Species.NewPests){
                headers.push("Number Seen");
            }

            // Add sighting summary for dog only.
            if (siteEventType === SiteEventType.Sighting && species === Species.WildDog){
                headers.push("Summary")
            }
        }

        // Add damage and control categories for dog, deer.
        if ((species === Species.WildDog || species === Species.Deer || species === Species.Rabbit ||
            species === Species.FeralCat || species === Species.Fox || species === Species.FeralPig ||
            species === Species.OtherPests)) {
                if (siteEventType === SiteEventType.Control) {
                    headers.push("Control Technique Used");
                }
                if (siteEventType === SiteEventType.Damage) {
                    headers.push("Damage Category");
                }
        }
        // End uncommon coloumns

        // Add the column for the comments
        headers.push("Notes");

        // Add the column for the edit link 
        headers.push("Edit");

        setTableHeaders(headers);
    }

    // Generate the site events request based off map filter values
    const siteEventsRequest = useMemo(() => {

        // Period
        let from = null;
        let to = null;
        switch (filter.reportingPeriod.period) {
            case MapFilterPeriodEnum.DateRange: 
                from = filter.reportingPeriod.fromField;
                to = filter.reportingPeriod.toField;
                break;

            case MapFilterPeriodEnum.Year: 
                const year = filter.reportingPeriod.yearField;
                from = year?.startOf('year');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (year?.year() === dayjs().year()) {
                    to = dayjs();
                } else {
                    to = year?.endOf('year');
                }

                break;
                
            case MapFilterPeriodEnum.MonthYear: 
                const monthYear = filter.reportingPeriod.monthYearField;
                from = monthYear?.startOf('month');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (monthYear?.year() === dayjs().year() && monthYear?.month() === dayjs().month()) {
                    to = dayjs();
                } else {
                    to = monthYear?.endOf('month');
                }

                break;

            case MapFilterPeriodEnum.LastSevenDays: 
                from = dayjs().subtract(7, 'days');  
                to = dayjs();  
                break;

            case MapFilterPeriodEnum.LastThirtyOneDays: 
                from = dayjs().subtract(31, 'days');  
                to = dayjs();  
                break;
        }

        switch (filter.bySubmitter.submitter) {
            case MapFilterSubmitter.AllData:
                return {
                    species: species,
                    siteEventType: siteEventType,
                    take: rowsPerPage,
                    skip: (rowsPerPage * page),
                    startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                    startDateOnOrBefore: to?.format('DD/MM/YYYY'),
                    locatedInRegionIds: (!isAdminOrSuperAdmin && isDataAccessForThisSpecies) ? filter.bySubmitter.locatedInRegionIds : undefined,
                    dataAccessId: (!isAdminOrSuperAdmin && isDataAccessForThisSpecies) ? filter.bySubmitter.dataAccessId : undefined,
                };
            case MapFilterSubmitter.PublicData:
                return {
                    species: species,
                    siteEventType: siteEventType,
                    take: rowsPerPage,
                    skip: (rowsPerPage * page),
                    onlyPublicData: true,
                    startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                    startDateOnOrBefore: to?.format('DD/MM/YYYY')
                };
            case MapFilterSubmitter.MyData:
                return {
                    species: species,
                    siteEventType: siteEventType,
                    take: rowsPerPage,
                    skip: (rowsPerPage * page),
                    userId: user?.id,
                    startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                    startDateOnOrBefore: to?.format('DD/MM/YYYY')
                };
            case MapFilterSubmitter.MyGroup:
                if (filter.bySubmitter?.communityGroupId){
                    if (filter.bySubmitter?.communityGroupMemberId && filter.bySubmitter?.communityGroupMemberId !== -1){
                        return {
                            species: species,
                            siteEventType: siteEventType,
                            userId: filter.bySubmitter.communityGroupMemberId,
                            memberOfCommunityGroupIds: [filter.bySubmitter.communityGroupId],
                            take: rowsPerPage,
                            skip: (rowsPerPage * page),
                            startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                            startDateOnOrBefore: to?.format('DD/MM/YYYY')
                        };
                    }
                    return {
                        species: species,
                        siteEventType: siteEventType,
                        memberOfCommunityGroupIds: [filter.bySubmitter.communityGroupId],
                        take: rowsPerPage,
                        skip: (rowsPerPage * page),
                        startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                        startDateOnOrBefore: to?.format('DD/MM/YYYY')
                    };
                }
        }
    }, [user, filter, species, page, rowsPerPage]);

    // Get site event requests
    const { data: siteEvents, loading: siteEventsLoading }  = useGetSiteEventGetCollection(
        siteEventsRequest, 
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    const handleEditClick = async (siteEvent: any) => {
        const { sightingTypeId, id, latitude, longitude } = siteEvent;
        if (!sightingTypeId || !id) return;

        const markerPromise = client.get(
            new GetSiteEventGetMarkerContent({
                species,
                siteEventType: sightingTypeId,
                id
            })
        );

        navigate(`/${speciesName}/map`);

        if (latitude != null && longitude != null) {
            setMapCenter({
                latitude: +latitude, 
                longitude: +longitude,
                zoom: 18
            });
        }

        const markerContent = await markerPromise;

        setRecordToEdit({
            siteEventType: sightingTypeId,
            recordId: id,
            hasImage: !!markerContent?.siteEventMarker?.marker?.galleryPhotoId
        });

        setSiteEventType(sightingTypeId as SiteEventType);

        openDrawer();
        maximiseDrawer();
    };

    // Generates the edit icon/button for a given site event
    const generateEditCell = (
        siteEvent: any,
        user: User | null,
        theme: Theme
    ) => {
        // Variable data for the edit link. You can edit if:
        // • it is your record
        // • you are an admin/superadmin
        var canEdit = false;
        if (user?.id == siteEvent.userId || (user?.roles?.includes("dataaccess") || user?.roles?.includes("admin"))) {
            canEdit = true;
        }

        if (canEdit) {
            return ( 
                <TableCell>
                    <FontAwesomeIcon 
                        icon={faPen}
                        style={{color: theme.palette.primary.main, cursor: "pointer"}}
                        onClick={async () => await handleEditClick(siteEvent)}
                    />
                </TableCell>
            )
            
        } else {
            return (
                <TableCell>
                    <FontAwesomeIcon 
                        icon={faPen}
                        style={{color: theme.palette.primary.main, cursor: "pointer"}}
                        onClick={() => addAlert({
                            content: <AlertTitle>
                                        You don't have permission to edit this record.
                                    </AlertTitle>,
                            dismissable: true,
                            severity: 'info',
                            timeout: SUCCESS_TIMEOUT
                        })}
                    />
                </TableCell>
            )
        }
    };

    const getSightingTableRows = (
            siteEvents: any[] | undefined, 
            lookups: Lookup[] | undefined, 
            species: Species, 
            user: User | null,
            theme: Theme
        ) => {
        let rows: JSX.Element[][] = [];
        let cells = []

        siteEvents?.map(siteEvent => {
            cells = [];
            var summaryValue = '';

            // Variable data for all species.
            var siteEventDate = undefined;

            if (siteEvent.startDateTime !== undefined) {
                siteEventDate = siteEvent.startDateTime;
            }

            var notes = siteEvent.comments !== undefined
                ? siteEvent.comments
                : '';

            // IACWEB-1111 - Only do for wild dogs, deer, fox, cat, rabbit, myna, newpests and pig.
            if (species === Species.WildDog || species === Species.Deer || species === Species.Fox ||
                species === Species.FeralCat || species === Species.FeralPig || species === Species.Rabbit ||
                species === Species.Myna || species === Species.NewPests) {

                // Abundance. For dog, deer, fox, cat, pig and rabbit: abundance & site number
                var abundanceScore = null;

                if (species === Species.WildDog) {
                    abundanceScore = siteEvent.wildDogAbundanceScore !== undefined
                        ? lookups?.find(x => x.name === 'WildDogAbundanceScore')?.items.find(x => x.id === siteEvent.wildDogAbundanceScore)?.value
                        : '';
                }
                else if (species === Species.Deer) {
                    abundanceScore = siteEvent.deerAbundanceScore;
                }
                else if (species === Species.Fox) {
                    abundanceScore = siteEvent.foxAbundanceScore !== undefined
                        ? lookups?.find(x => x.name === 'FoxAbundanceScore')?.items.find(x => x.id === siteEvent.foxAbundanceScore)?.value
                        : '';
                }
                else if (species === Species.FeralCat) {
                    abundanceScore = siteEvent.feralCatAbundanceScore !== undefined
                        ? lookups?.find(x => x.name === 'FeralCatAbundanceScore')?.items.find(x => x.id === siteEvent.feralCatAbundanceScore)?.value
                        : '';
                }
                else if (species === Species.Rabbit) {
                    abundanceScore = siteEvent.rabbitAbundanceScore !== undefined
                        ? lookups?.find(x => x.name === 'RabbitAbundanceScore')?.items.find(x => x.id === siteEvent.rabbitAbundanceScore)?.value
                        : '';
                }
                else if (species === Species.FeralPig) {
                    abundanceScore = siteEvent.feralPigAbundanceScore;
                }

                // Site number
                var siteNumber = siteEvent.siteNumber !== undefined
                    ? siteEvent.siteNumber
                    : '';

                // For dog, deer, cat and fox: property name
                if (species === Species.WildDog || species === Species.Deer
                    || species === Species.FeralCat || Species.FeralPig 
                    || species === Species.Fox) {
                    var propertyName = siteEvent.propertyName !== undefined
                        ? siteEvent.propertyName
                        : '';
                }

                // For dog, deer, fox, pig and cat: find the sighting type
                var sightingType = null;

                if (species === Species.WildDog) {
                    sightingType = siteEvent.wildDogSightingType !== undefined
                        ? lookups?.find(x => x.name === 'WildDogSightingType')?.items.find(x => x.id === siteEvent.wildDogSightingType)?.value
                        : 0;
                }
                else if (species === Species.Deer) {
                    sightingType = siteEvent.deerSightingType !== undefined
                        ? lookups?.find(x => x.name === 'DeerSightingType')?.items.find(x => x.id === siteEvent.deerSightingType)?.value
                        : 0;
                }
                else if (species === Species.Fox) {
                    sightingType = siteEvent.foxSightingType !== undefined
                        ? lookups?.find(x => x.name === 'FoxSightingType')?.items.find(x => x.id === siteEvent.foxSightingType)?.value
                        : 0;
                }
                else if (species === Species.FeralCat) {
                    sightingType = siteEvent.feralCatSightingType !== undefined
                        ? lookups?.find(x => x.name === 'FeralCatSightingType')?.items.find(x => x.id === siteEvent.feralCatSightingType)?.value
                        : 0;
                }
                else if (species === Species.FeralPig) {
                    sightingType = siteEvent.feralPigSightingType !== undefined
                        ? lookups?.find(x => x.name === 'FeralPigSightingType')?.items.find(x => x.id === siteEvent.feralPigSightingType)?.value
                        : 0;
                }

                // Start summary.
                if (species === Species.WildDog) {
                    var age = siteEvent.wildDogSightingAgeScore !== undefined 
                                ? lookups?.find(x => x.name === 'WildDogSightingAgeScore')?.items.find(x => x.id === siteEvent.wildDogSightingAgeScore)?.value
                                : '';
                    var colour = siteEvent.colour !== undefined 
                                ? lookups?.find(x => x.name === 'Colour')?.items.find(x => x.id === siteEvent.colour)?.value
                                : '';
                    var condiser = siteEvent.recommendationConsiderScore !== undefined 
                                    ? lookups?.find(x => x.name === 'RecommendationConsiderScore')?.items.find(x => x.id === siteEvent.recommendationConsiderScore)?.value
                                    : '';
                    var monitor = siteEvent.recommendationMonitorScore !== undefined 
                                    ? lookups?.find(x => x.name === 'RecommendationMonitorScore')?.items.find(x => x.id === siteEvent.recommendationMonitorScore)?.value
                                    : '';
                    var sex = siteEvent.sex !== undefined 
                                ? lookups?.find(x => x.name === 'Sex')?.items.find(x => x.id === siteEvent.sex)?.value
                                : '';

                    if (age !== undefined && age !== '')
                        summaryValue += "Age: " + age + "<br> ";
                    if (colour !== undefined && colour !== '')
                        summaryValue += "Colour: " + colour + "<br> ";
                    if (condiser !== undefined && condiser !== '')
                        summaryValue += "Consider: " + condiser + "<br> ";
                    if (monitor !== undefined && monitor !== '')
                        summaryValue += "Monitor: " + monitor + "<br> ";
                    if (sex !== undefined && sex !== '')
                        summaryValue += "Sex: " + sex + "<br> ";
                }
            }

            // Set up the elements for common cells of all species.
            cells.push(
                <TableCell>
                    {dayjs(siteEventDate).format("DD/MM/YYYY")}
                </TableCell>
            );

            // Add uncommon cells for: dog, deer, fox, cat, rabbit, myna, new pests and pig
            if (species === Species.WildDog || species === Species.Deer || species === Species.Fox || 
                species === Species.FeralCat || species === Species.Rabbit || species === Species.Myna || 
                species === Species.NewPests || species === Species.FeralPig) {
                // Add cells that are special to dog, fox, cat, pig and deer only
                if (species === Species.WildDog || species === Species.Fox || species === Species.FeralCat || 
                    species === Species.FeralPig || species === Species.Deer ) {
                    cells.push(
                        <TableCell>
                            {propertyName}
                        </TableCell>
                    );
                }

                // Add cells that are common for all except rabbit, myna, newpests: sighting type
                if (species !== Species.Rabbit && species !== Species.Myna && species !== Species.NewPests) {
                    cells.push(
                        <TableCell>
                            {sightingType}
                        </TableCell>
                    );
                }

                // Add cells that are common for all except cat: site number
                if (species !== Species.FeralCat ) {
                    cells.push(
                        <TableCell>
                            {siteNumber}
                        </TableCell>
                    );
                }

                // Add the rest of the uncommon cells, except myna, new pests.
                if (species !== Species.Myna && species !== Species.NewPests) {
                    cells.push(
                        <TableCell>
                            {abundanceScore}
                        </TableCell>
                    );
                }
                
                // Add summary for dogs only.
                if (species === Species.WildDog) {
                    cells.push(
                        <TableCell>
                            { parse(DOMPurify.sanitize(summaryValue)) }
                        </TableCell>
                    );
                }
            }
            // Add the comments and edit column for all species.
            cells.push(
                <TableCell>
                    {notes}
                </TableCell>
            );
            cells.push(generateEditCell(siteEvent, user, theme));
        
            rows.push(cells);
        });
        return (
            <>
                {rows?.map((row, index) => (
                    <TableRow key={`sighting-${index}`}>
                        {row.map((cell: any, index) => (
                            <Fragment key={`sighting-${index}-cell`}>{cell}</Fragment>
                        ))}
                    </TableRow>
                ))}
            </>
        );
    };

    const getDamageTableRows = (
        siteEvents: any[] | undefined, 
        species: Species, 
        user: User | null,
        theme: Theme
    ) => {
        let rows: JSX.Element[][] = [];
        let cells = []

        siteEvents?.map(siteEvent => {
            cells = [];
            var summaryValue = '';

            // Variable data for all species.
            var siteEventDate = undefined;

            if (siteEvent.startDateTime !== undefined)
                siteEventDate = siteEvent.startDateTime;

            var notes = siteEvent.comments !== undefined
                ? siteEvent.comments
                : '';
            
            // IACWEB-1111 - Only do for wild dogs, deer, fox, myna, newpests, cat, pig and rabbit.
            if (species === Species.WildDog || species === Species.Deer || species === Species.Fox || species === Species.Myna || 
                species === Species.NewPests || species === Species.FeralCat || species === Species.Rabbit || species === Species.FeralPig){
                // Site number: except myna and cat
                if (species !== Species.Myna && species !== Species.NewPests) {
                    var siteNumber = siteEvent.siteNumber !== undefined
                        ? siteEvent.siteNumber
                        : '';
                }

                // Property name: dog, fox, cat, pig, deer only
                if (species === Species.WildDog || species === Species.Fox || species === Species.FeralCat ||
                    species === Species.FeralPig || species === Species.Deer) {
                    var propertyName = siteEvent.propertyName !== undefined
                        ? siteEvent.propertyName
                        : '';
                }
            }

            // IACWEB-1111 - Wild dogs damage category
            if (species === Species.WildDog) {
                // Site number
                var siteNumber = siteEvent.siteNumber !== undefined
                        ? siteEvent.siteNumber
                        : '';

                // Property name
                var propertyName = siteEvent.propertyName !== undefined
                        ? siteEvent.propertyName
                        : '';

                // Damage Summary
                //Sheep
                if (siteEvent.damageSheep !== undefined && siteEvent.damageSheep !== false) {
                    summaryValue += "Damaged Sheep: " + siteEvent.damageSheep + "<br> ";
                    if (siteEvent.damageSheepNumberKilled !== undefined && siteEvent.damageSheepNumberKilled !== '')
                        summaryValue += "Sheeps Killed: " + siteEvent.damageSheepNumberKilled + "<br> ";
                    if (siteEvent.damageSheepNumberMauled !== undefined && siteEvent.damageSheepNumberMauled !== '')
                        summaryValue += "Sheeps Mauled: " + siteEvent.damageSheepNumberMauled + "<br> ";
                }
                //Goats
                if (siteEvent.damageGoats !== undefined && siteEvent.damageGoats !== false) {
                    summaryValue += "Damaged Goats: " + siteEvent.damageGoats + "<br> ";
                    if (siteEvent.damageGoatsNumberKilled !== undefined && siteEvent.damageGoatsNumberKilled !== '')
                        summaryValue += "Goats Killed: " + siteEvent.damageGoatsNumberKilled + "<br> ";
                    if (siteEvent.damageGoatsNumberMauled !== undefined && siteEvent.damageGoatsNumberMauled !== '')
                        summaryValue += "Goats Mauled: " + siteEvent.damageGoatsNumberMauled + "<br> ";
                }
                //GoatKids
                if (siteEvent.damageGoatKids !== undefined && siteEvent.damageGoatKids !== false) {
                    summaryValue += "Damaged Goat Kids: " + siteEvent.damageGoatKids + "<br> ";
                    if (siteEvent.damageGoatKidsNumberKilled !== undefined && siteEvent.damageGoatKidsNumberKilled !== '')
                        summaryValue += "Goat Kids Killed: " + siteEvent.damageGoatKidsNumberKilled + "<br> ";
                    if (siteEvent.damageGoatKidsNumberMauled !== undefined && siteEvent.damageGoatKidsNumberMauled !== '')
                        summaryValue += "Goat Kids Mauled: " + siteEvent.damageGoatKidsNumberMauled + "<br> ";
                }
                //Lambs
                if (siteEvent.damageLambs !== undefined && siteEvent.damageLambs !== false) {
                    summaryValue += "Damaged Lambs: " + siteEvent.damageLambs + "<br> ";
                    if (siteEvent.damageLambsNumberKilled !== undefined && siteEvent.damageLambsNumberKilled !== '')
                        summaryValue += "Lambs Killed: " + siteEvent.damageLambsNumberKilled + "<br> ";
                    if (siteEvent.damageLambsNumberMauled !== undefined && siteEvent.damageLambsNumberMauled !== '')
                        summaryValue += "Lambs Mauled: " + siteEvent.damageLambsNumberMauled + "<br> ";
                }
                //damageCalves
                if (siteEvent.damageCalves !== undefined && siteEvent.damageCalves !== false) {
                    summaryValue += "Damaged Calves: " + siteEvent.damageCalves + "<br> ";
                    if (siteEvent.calvesNumberKilled !== undefined && siteEvent.calvesNumberKilled !== '')
                        summaryValue += "Calves Killed: " + siteEvent.calvesNumberKilled + "<br> ";
                    if (siteEvent.calvesNumberMauled !== undefined && siteEvent.calvesNumberMauled !== '')
                        summaryValue += "Calves Mauled: " + siteEvent.calvesNumberMauled + "<br> ";
                }
                //damageCattle
                if (siteEvent.damageCattle !== undefined && siteEvent.damageCattle !== false) {
                    summaryValue += "Damaged Cattle: " + siteEvent.damageCattle + "<br> ";
                    if (siteEvent.damageCattleNumberKilled !== undefined && siteEvent.damageCattleNumberKilled !== '')
                        summaryValue += "Cattle Killed: " + siteEvent.damageCattleNumberKilled + "<br> ";
                    if (siteEvent.damageCattleNumberMauled !== undefined && siteEvent.damageCattleNumberMauled !== '')
                        summaryValue += "Cattle Mauled: " + siteEvent.damageCattleNumberMauled + "<br> ";
                }
                //damagePoultry
                if (siteEvent.damagePoultry !== undefined && siteEvent.damagePoultry !== false) {
                    summaryValue += "Damaged Poultry: " + siteEvent.damagePoultry + "<br> ";
                    if (siteEvent.damagePoultryNumberKilled !== undefined && siteEvent.damagePoultryNumberKilled !== '')
                        summaryValue += "Poultry Killed: " + siteEvent.damagePoultryNumberKilled + "<br> ";
                    if (siteEvent.damagePoultryNumberMauled !== undefined && siteEvent.damagePoultryNumberMauled !== '')
                        summaryValue += "Poultry Mauled: " + siteEvent.damagePoultryNumberMauled + "<br> ";
                }
                //damageDomestic
                if (siteEvent.damageDomestic !== undefined && siteEvent.damageDomestic !== false) {
                    summaryValue += "Damaged Domestic: " + siteEvent.damageDomestic + "<br> ";
                    if (siteEvent.damageDomesticNumberKilled !== undefined && siteEvent.damageDomesticNumberKilled !== '')
                        summaryValue += "Domestic Killed: " + siteEvent.damageDomesticNumberKilled + "<br> ";
                    if (siteEvent.damageDomesticNumberMauled !== undefined && siteEvent.damageDomesticNumberMauled !== '')
                        summaryValue += "Domestic Mauled: " + siteEvent.damageDomesticNumberMauled + "<br> ";
                }
                //damageDogs
                if (siteEvent.damageDogs !== undefined && siteEvent.damageDogs !== false) {
                    summaryValue += "Damaged Dog: " + siteEvent.damageDogs + "<br> ";
                    if (siteEvent.damageDogsNumberKilled !== undefined && siteEvent.damageDogsNumberKilled !== '')
                        summaryValue += "Dog Killed: " + siteEvent.damageDogsNumberKilled + "<br> ";
                    if (siteEvent.damageDomesticNumberMauled !== undefined && siteEvent.damageDomesticNumberMauled !== '')
                        summaryValue += "Dog Mauled: " + siteEvent.damageDomesticNumberMauled + "<br> ";
                }
                //damageNative
                if (siteEvent.damageNative !== undefined && siteEvent.damageNative !== false) {
                    summaryValue += "Damaged Native: " + siteEvent.damageNative + "<br> ";
                }
                //damageHuman
                if (siteEvent.damageHuman !== undefined && siteEvent.damageHuman !== false) {
                    summaryValue += "Damaged Human: " + siteEvent.damageHuman + "<br> ";
                    if (siteEvent.damageAttackHuman !== undefined && siteEvent.damageAttackHuman !== false)
                        summaryValue += "Human Attacked: " + siteEvent.damageAttackHuman + "<br> ";
                }
                //damageRubbish
                if (siteEvent.damageRubbish !== undefined && siteEvent.damageRubbish !== false) {
                    summaryValue += "Damaged Rubbish: " + siteEvent.damageRubbish + "<br> ";
                }
                //damageHorses
                if (siteEvent.damageHorses !== undefined && siteEvent.damageHorses !== false) {
                    summaryValue += "Damaged Horses: " + siteEvent.damageHorses + "<br> ";
                }
                //damageOther
                if (siteEvent.damageOther !== undefined && siteEvent.damageOther !== false) {
                    summaryValue += "Damaged Other";
                    if (siteEvent.damageOtherSpec !== undefined && siteEvent.damageOtherSpec !== '')
                        summaryValue += ": " + siteEvent.damageOtherSpec + "<br> ";
                }
            }

            // IACWEB-1111 - Deer damage category.
            if (species === Species.Deer) {
                if (siteEvent.damageLivestock !== undefined && siteEvent.damageLivestock !== false) {
                    summaryValue += "Competition with livestock.<br>";
                }

                if (siteEvent.damageCrops !== undefined && siteEvent.damageCrops !== false) {
                    summaryValue += "Damaged crops.<br>";
                }

                if (siteEvent.damageNativeVegetation !== undefined && siteEvent.damageNativeVegetation !== false) {
                    summaryValue += "Damage to native vegetation.<br>";
                }

                if (siteEvent.damageTreePlanting !== undefined && siteEvent.damageTreePlanting !== false) {
                    summaryValue += "Damage to tree planting activities.<br>";
                }

                if (siteEvent.damageWeeds !== undefined && siteEvent.damageWeeds !== false) {
                    summaryValue += "Spread of weeds and seeds.<br>";
                }

                if (siteEvent.damageSoil !== undefined && siteEvent.damageSoil !== false) {
                    summaryValue += "Soil disturbance and erosion.<br>";
                }

                if (siteEvent.damageFences !== undefined && siteEvent.damageFences !== false) {
                    summaryValue += "Damage to fences and gates.<br>";
                }

                if (siteEvent.damageBrowsingSensitive !== undefined && siteEvent.damageBrowsingSensitive !== false) {
                    summaryValue += "Sensitive vegetation.<br>";
                }

                if (siteEvent.damageWater !== undefined && siteEvent.damageWater !== false) {
                    summaryValue += "Fouling of water sources.<br>";
                }

                if (siteEvent.damageDisease !== undefined && siteEvent.damageDisease !== false) {
                    summaryValue += "Disease.<br>";
                }

                if (siteEvent.damageDiseaseSpread !== undefined && siteEvent.damageDiseaseSpread !== false) {
                    summaryValue += "Spread livestock diseases.<br>";
                }

                if (siteEvent.damageSacred !== undefined && siteEvent.damageSacred !== false) {
                    summaryValue += "Damage to traditional areas and sacred sites.<br>";
                }

                if (siteEvent.damageTucker !== undefined && siteEvent.damageTucker !== false) {
                    summaryValue += "Damaged Tucker <br>";
                }

                if (siteEvent.damageEcoTourism !== undefined && siteEvent.damageEcoTourism !== false) {
                    summaryValue += "Impact on eco-tourism.<br>";
                }

                if (siteEvent.damageNearCollisionVehicle !== undefined && siteEvent.damageNearCollisionVehicle !== false) {
                    summaryValue += "Near collision with vehicle.<br>";
                }

                if (siteEvent.damageVehicle !== undefined && siteEvent.damageVehicle !== false) {
                    summaryValue += "Motor vehicle collision or hazard.<br>";
                }

                if (siteEvent.damageNativeHerbivores !== undefined && siteEvent.damageNativeHerbivores !== false) {
                    summaryValue += "Competition with native herbivores.<br>";
                }

                if (siteEvent.damagePlantation !== undefined && siteEvent.damagePlantation !== false) {
                    summaryValue += "Plantation damage.<br>";
                }

                if (siteEvent.damageFruitTree !== undefined && siteEvent.damageFruitTree !== false) {
                    summaryValue += "Damaged Fruit Tree <br>";
                }

                if (siteEvent.damageVineyard !== undefined && siteEvent.damageVineyard !== false) {
                    summaryValue += "Vineyard damage.<br>";
                }

                if (siteEvent.damageTrampleCrops !== undefined && siteEvent.damageTrampleCrops !== false) {
                    summaryValue += "Trample crops <br>";
                }

                if (siteEvent.damageNutCereal !== undefined && siteEvent.damageNutCereal !== false) {
                    summaryValue += "Nut and cereal crop damage.<br>";
                }

                if (siteEvent.damageVegeCrop !== undefined && siteEvent.damageVegeCrop !== false) {
                    summaryValue += "Vegetable crop impacts (such as potatos.<br>";
                }

                if (siteEvent.damagedamageMarketGarden !== undefined && siteEvent.damagedamageMarketGarden !== false) {
                    summaryValue += "Market garden damage.<br>";
                }

                if (siteEvent.damageAggressive !== undefined && siteEvent.damageAggressive !== false) {
                    summaryValue += "Aggressive behaviour towards humans.<br>";
                }

                if (siteEvent.damageFlowers !== undefined && siteEvent.damageFlowers !== false) {
                    summaryValue += "Damage to gardens / flowers.<br>";
                }

                if (siteEvent.damageOther !== undefined && siteEvent.damageOther !== false) {
                    summaryValue += "Damaged other";
                    if (siteEvent.damageOtherSpec !== undefined && siteEvent.damageOtherSpec !== '')
                        summaryValue += ": " + siteEvent.damageOtherSpec;
                    else
                        summaryValue += ".";
                }
            }

            // IACWEB-1111 - Rabbit damage category.
            if (species === Species.Rabbit) {
                if (siteEvent.damageCompetitionPasture !== undefined && siteEvent.damageCompetitionPasture !== false) {
                    summaryValue += "Competition for pasture.<br>";
                }

                if (siteEvent.damageSoil !== undefined && siteEvent.damageSoil !== false) {
                    summaryValue += "Soil erosion / land degradation.<br>";
                }

                if (siteEvent.damageProductivity !== undefined && siteEvent.damageProductivity !== false) {
                    summaryValue += "Agricultural productivity losses.<br>";
                }

                if (siteEvent.damageBuildings !== undefined && siteEvent.damageBuildings !== false) {
                    summaryValue += "Damage foundations of buildings.<br>";
                }

                if (siteEvent.damageNativeVegetation !== undefined && siteEvent.damageNativeVegetation !== false) {
                    summaryValue += "Damage to native vegetation.<br>";
                }

                if (siteEvent.damageWeeds !== undefined && siteEvent.damageWeeds !== false) {
                    summaryValue += "Spread of weeds.<br>";
                }

                if (siteEvent.damageCompetitionAnimals !== undefined && siteEvent.damageCompetitionAnimals !== false) {
                    summaryValue += "Competition with native animals.<br>";
                }

                if (siteEvent.damageSites !== undefined && siteEvent.damageSites !== false) {
                    summaryValue += "Damage to significant sites.<br>";
                }

                if (siteEvent.damageGarden !== undefined && siteEvent.damageGarden !== false) {
                    summaryValue += "Damage to garden.<br>";
                }

                if (siteEvent.damageBiodiversity !== undefined && siteEvent.damageBiodiversity !== false) {
                    summaryValue += "Biodiversity impacts.<br>";
                }

                if (siteEvent.damageCrops !== undefined && siteEvent.damageCrops !== false) {
                    summaryValue += "Damage to horticulture or crops.<br>";
                }

                if (siteEvent.damagePrograms !== undefined && siteEvent.damagePrograms !== false) {
                    summaryValue += "Damage to tree planting.<br>";
                }

                if (siteEvent.damageVehicle !== undefined && siteEvent.damageVehicle !== false) {
                    summaryValue += "Hazard (eg. warren holes).<br>";
                }

                if (siteEvent.damageSport !== undefined && siteEvent.damageSport !== false) {
                    summaryValue += "Damage to sporting fields.<br>";
                }

                if (siteEvent.damageDigging !== undefined && siteEvent.damageDigging !== false) {
                    summaryValue += "Diggings, scratchings, soil disturbance.<br>";
                }

                if (siteEvent.damageOther !== undefined && siteEvent.damageOther !== false) {
                    summaryValue += "Damaged other";
                    if (siteEvent.damageOtherSpec !== undefined && siteEvent.damageOtherSpec !== '')
                        summaryValue += ": " + siteEvent.damageOtherSpec + "<br> ";
                    else
                        summaryValue += ".<br> ";
                }
            }

            // IACWEB-1111 - Feral cat damage category.
            if (species === Species.FeralCat) {
                if (siteEvent.damageNative !== undefined && siteEvent.damageNative !== false) {
                    summaryValue += "Kill or injure native wildlife";

                    if (siteEvent.damageNativeBirds !== undefined && siteEvent.damageNativeBirds !== ''
                        || siteEvent.damageNativeMammals !== undefined && siteEvent.damageNativeMammals !== ''
                        || siteEvent.damageNativeReptiles !== undefined && siteEvent.damageNativeReptiles !== ''
                        || siteEvent.damageNativeFrogs !== undefined && siteEvent.damageNativeFrogs !== '')
                        summaryValue += ":<br>";
                    else
                        summaryValue += ".<br>";

                    if (siteEvent.damageNativeBirds !== undefined && siteEvent.damageNativeBirds !== '')
                        summaryValue += "Birds<br>";

                    if (siteEvent.damageNativeMammals !== undefined && siteEvent.damageNativeMammals !== '')
                        summaryValue += "Mammals<br>";

                    if (siteEvent.damageNativeReptiles !== undefined && siteEvent.damageNativeReptiles !== '')
                        summaryValue += "Reptiles<br>";

                    if (siteEvent.damageNativeFrogs !== undefined && siteEvent.damageNativeFrogs !== '')
                        summaryValue += "Frogs<br>";
                    summaryValue += "<br>";
                }

                if (siteEvent.damageDomestic !== undefined && siteEvent.damageDomestic !== false) {
                    summaryValue += "Kill or injure domestic animals";

                    if (siteEvent.damageDomesticLambs !== undefined && siteEvent.damageDomesticLambs !== ''
                        || siteEvent.damageDomesticGoats !== undefined && siteEvent.damageDomesticGoats !== ''
                        || siteEvent.damageDomesticPoultry !== undefined && siteEvent.damageDomesticPoultry !== ''
                        || siteEvent.damageDomesticBirds !== undefined && siteEvent.damageDomesticBirds !== ''
                        || siteEvent.damageDomesticOther !== undefined && siteEvent.damageDomesticOther !== '')
                        summaryValue += ":<br>";
                    else
                        summaryValue += ".<br>";

                    if (siteEvent.damageDomesticLambs !== undefined && siteEvent.damageDomesticLambs !== '')
                        summaryValue += "Lambs<br>";

                    if (siteEvent.damageDomesticGoats !== undefined && siteEvent.damageDomesticGoats !== '')
                        summaryValue += "Goats<br>";

                    if (siteEvent.damageDomesticPoultry !== undefined && siteEvent.damageDomesticPoultry !== '')
                        summaryValue += "Poultry<br>";

                    if (siteEvent.damageDomesticBirds !== undefined && siteEvent.damageDomesticBirds !== '')
                        summaryValue += "Birds<br>";

                    if (siteEvent.damageDomesticOther !== undefined && siteEvent.damageDomesticOther !== '')
                    {
                        summaryValue += "Other";
                        if (siteEvent.damageDomesticOtherSpec !== undefined && siteEvent.damageDomesticOtherSpec !== '')
                            summaryValue += ": " + siteEvent.damageDomesticOtherSpec + "<br>";
                        else
                            summaryValue += "<br>";
                    }
                    summaryValue += "<br>";
                }

                if (siteEvent.damageAnnoyance !== undefined && siteEvent.damageAnnoyance !== false) {
                    summaryValue += "General annoyance.<br>";
                }

                if (siteEvent.damageOther !== undefined && siteEvent.damageOther !== false) {
                    summaryValue += "Damaged other";
                    if (siteEvent.damageOtherSpec !== undefined && siteEvent.damageOtherSpec !== '')
                        summaryValue += ": " + siteEvent.damageOtherSpec;
                    else
                        summaryValue += ".";
                }
            }

            // IACWEB-1111 - Fox damage category.
            if (species === Species.Fox) {
                //Sheep
                if (siteEvent.damageSheep !== undefined && siteEvent.damageSheep !== false) {
                    summaryValue += "Kill or maul sheep<br> ";
                    if (siteEvent.damageSheepNumberKilled !== undefined && siteEvent.damageSheepNumberKilled !== '')
                        summaryValue += "Sheeps Killed: " + siteEvent.damageSheepNumberKilled + "<br> ";
                    if (siteEvent.damageSheepNumberMauled !== undefined && siteEvent.damageSheepNumberMauled !== '')
                        summaryValue += "Sheeps Mauled: " + siteEvent.damageSheepNumberMauled + "<br><br>";
                }
                //Lambs
                if (siteEvent.damageLambs !== undefined && siteEvent.damageLambs !== false) {
                    summaryValue += "Kill or maul lambs: " + siteEvent.damageLambs + "<br> ";
                    if (siteEvent.damageLambsNumberKilled !== undefined && siteEvent.damageLambsNumberKilled !== '')
                        summaryValue += "Lambs Killed: " + siteEvent.damageLambsNumberKilled + "<br> ";
                    if (siteEvent.damageLambsNumberMauled !== undefined && siteEvent.damageLambsNumberMauled !== '')
                        summaryValue += "Lambs Mauled: " + siteEvent.damageLambsNumberMauled + "<br><br>";
                }
                //Goats
                if (siteEvent.damageGoats !== undefined && siteEvent.damageGoats !== false) {
                    summaryValue += "Kill or maul goats: " + siteEvent.damageGoats + "<br> ";
                    if (siteEvent.damageGoatsNumberKilled !== undefined && siteEvent.damageGoatsNumberKilled !== '')
                        summaryValue += "Goats Killed: " + siteEvent.damageGoatsNumberKilled + "<br> ";
                    if (siteEvent.damageGoatsNumberMauled !== undefined && siteEvent.damageGoatsNumberMauled !== '')
                        summaryValue += "Goats Mauled: " + siteEvent.damageGoatsNumberMauled + "<br><br>";
                }
                //GoatKids
                if (siteEvent.damageGoatKids !== undefined && siteEvent.damageGoatKids !== false) {
                    summaryValue += "Damaged Goat Kids: " + siteEvent.damageGoatKids + "<br> ";
                    if (siteEvent.damageGoatKidsNumberKilled !== undefined && siteEvent.damageGoatKidsNumberKilled !== '')
                        summaryValue += "Goat Kids Killed: " + siteEvent.damageGoatKidsNumberKilled + "<br> ";
                    if (siteEvent.damageGoatKidsNumberMauled !== undefined && siteEvent.damageGoatKidsNumberMauled !== '')
                        summaryValue += "Goat Kids Mauled: " + siteEvent.damageGoatKidsNumberMauled + "<br><br>";
                }
                if (siteEvent.damagePoultry !== undefined && siteEvent.damagePoultry !== false) {
                    summaryValue += "Kill or maul poultry: " + siteEvent.damagePoultry + "<br> ";
                    if (siteEvent.damagePoultryNumberKilled !== undefined && siteEvent.damagePoultryNumberKilled !== '')
                        summaryValue += "Poultry Killed: " + siteEvent.damagePoultryNumberKilled + "<br> ";
                    if (siteEvent.damagePoultryNumberMauled !== undefined && siteEvent.damagePoultryNumberMauled !== '')
                        summaryValue += "Poultry Mauled: " + siteEvent.damagePoultryNumberMauled + "<br><br>";
                }
                //damageNative
                if (siteEvent.damageNative !== undefined && siteEvent.damageNative !== false) {
                    summaryValue += "Kill or maul native wildlife.<br> ";
                }
                //damageDomestic
                if (siteEvent.damageDomestic !== undefined && siteEvent.damageDomestic !== false) {
                    summaryValue += "Attack pets or domestic animals: " + siteEvent.damageDomestic + "<br> ";
                    if (siteEvent.damageDomesticNumberKilled !== undefined && siteEvent.damageDomesticNumberKilled !== '')
                        summaryValue += "Domestic Killed: " + siteEvent.damageDomesticNumberKilled + "<br> ";
                    if (siteEvent.damageDomesticNumberMauled !== undefined && siteEvent.damageDomesticNumberMauled !== '')
                        summaryValue += "Domestic Mauled: " + siteEvent.damageDomesticNumberMauled + "<br><br>";
                }
                //damageNative
                if (siteEvent.damageWeeds !== undefined && siteEvent.damageWeeds !== false) {
                    summaryValue += "Spread weeds.<br> ";
                }

                if (siteEvent.damageOther !== undefined && siteEvent.damageOther !== false) {
                    summaryValue += "Damaged other";
                    if (siteEvent.damageOtherSpec !== undefined && siteEvent.damageOtherSpec !== '')
                        summaryValue += ": " + siteEvent.damageOtherSpec;
                    else
                        summaryValue += ".";
                }
            }

            // IACWEB-1111 - Feral pig damage category.
            if (species === Species.FeralPig) {

                if (siteEvent.damagePredationLambs !== undefined && siteEvent.damagePredationLambs !== false) {
                    summaryValue += "Kill or maul lambs.<br> ";
                }

                if (siteEvent.damageLivestock !== undefined && siteEvent.damageLivestock !== false) {
                    summaryValue += "Competition with livestock.<br> ";
                }

                if (siteEvent.damageCrops !== undefined && siteEvent.damageCrops !== false) {
                    summaryValue += "Damage to crops and cereals.<br> ";
                }

                if (siteEvent.damageFruit !== undefined && siteEvent.damageFruit !== false) {
                    summaryValue += "Damage to fruit crops (eg. bananas).<br> ";
                }

                if (siteEvent.damageNative !== undefined && siteEvent.damageNative !== false) {
                    summaryValue += "Predation of native animals (eg. turtle eggs).<br> ";
                }

                if (siteEvent.damageWeeds !== undefined && siteEvent.damageWeeds !== false) {
                    summaryValue += "Spread of weeds and seeds.<br> ";
                }

                if (siteEvent.damageSoil !== undefined && siteEvent.damageSoil !== false) {
                    summaryValue += "Soil disturbance and erosion.<br> ";
                }

                if (siteEvent.damageDiseaseSpread !== undefined && siteEvent.damageDiseaseSpread !== false) {
                    summaryValue += "Spread livestock diseases.<br> ";
                }

                if (siteEvent.damageWetlands !== undefined && siteEvent.damageWetlands !== false) {
                    summaryValue += "Damage to sensitive wetlands and flora.<br> ";
                }

                if (siteEvent.damageWater !== undefined && siteEvent.damageWater !== false) {
                    summaryValue += "Damage to waterholes, bores and irrigation channels.<br> ";
                }

                if (siteEvent.damageSacred !== undefined && siteEvent.damageSacred !== false) {
                    summaryValue += "Damage to traditional areas and sacred sites.<br> ";
                }

                if (siteEvent.damageTucker !== undefined && siteEvent.damageTucker !== false) {
                    summaryValue += "Damage to bush tucker.<br> ";
                }

                if (siteEvent.damageFences !== undefined && siteEvent.damageFences !== false) {
                    summaryValue += "Damage to fences.<br> ";
                }

                if (siteEvent.damageHuman !== undefined && siteEvent.damageHuman !== false) {
                    summaryValue += "Human health risk (eg. safety).<br> ";
                }

                if (siteEvent.damageEcoTourism !== undefined && siteEvent.damageEcoTourism !== false) {
                    summaryValue += "Impact on eco-tourism values.<br> ";
                }

                if (siteEvent.damageOther !== undefined && siteEvent.damageOther !== false) {
                    summaryValue += "Damaged other";
                    if (siteEvent.damageOtherSpec !== undefined && siteEvent.damageOtherSpec !== '')
                        summaryValue += ": " + siteEvent.damageOtherSpec;
                    else
                        summaryValue += ".";
                }
            }

            // IACWEB-1111 - Other pests damage category.
            if (species === Species.OtherPests) {
                if (siteEvent.damageRoad !== undefined && siteEvent.damageRoad !== false) {
                    summaryValue += "Road vehicle hazard.<br> ";
                }

                if (siteEvent.damageSafety !== undefined && siteEvent.damageSafety !== false) {
                    summaryValue += "Direct threat to public safety.<br> ";
                }

                if (siteEvent.damageLivestockCompetition !== undefined && siteEvent.damageLivestockCompetition !== false) {
                    summaryValue += "Competition with livestock.<br> ";
                }

                if (siteEvent.damageNativeVegetation !== undefined && siteEvent.damageNativeVegetation !== false) {
                    summaryValue += "Damage to native vegetation or regeneration.<br> ";
                }

                if (siteEvent.damageSensitiveVegetation !== undefined && siteEvent.damageSensitiveVegetation !== false) {
                    summaryValue += "Trample sensitive vegetation.<br> ";
                }

                if (siteEvent.damageWeeds !== undefined && siteEvent.damageWeeds !== false) {
                    summaryValue += "Spread weeds.<br> ";
                }

                if (siteEvent.damageWetland !== undefined && siteEvent.damageWetland !== false) {
                    summaryValue += "Damage to wetlands.<br> ";
                }

                if (siteEvent.damageBiodiversity !== undefined && siteEvent.damageBiodiversity !== false) {
                    summaryValue += "Biodiversity impacts.<br> ";
                }

                if (siteEvent.damageCompetition !== undefined && siteEvent.damageCompetition !== false) {
                    summaryValue += "Competition with native animals.<br> ";
                }

                if (siteEvent.damageWater !== undefined && siteEvent.damageWater !== false) {
                    summaryValue += "Damage to water infrastructure.<br> ";
                }

                if (siteEvent.damageBuildings !== undefined && siteEvent.damageBuildings !== false) {
                    summaryValue += "Damage to buildings/sheds.<br> ";
                }

                if (siteEvent.damageFences!== undefined && siteEvent.damageLivestock !== false) {
                    summaryValue += "Damage to fences, gates, yards.<br> ";
                }

                if (siteEvent.damageCultural !== undefined && siteEvent.damageCultural !== false) {
                    summaryValue += "Damage to sites of cultural significance.<br> ";
                }

                if (siteEvent.damageTucker !== undefined && siteEvent.damageTucker !== false) {
                    summaryValue += "Damage to bush tucker.<br> ";
                }

                if (siteEvent.damageLivestockDisease !== undefined && siteEvent.damageLivestockDisease !== false) {
                    summaryValue += "Spread livestock disease.<br> ";
                }

                if (siteEvent.damageNuisance !== undefined && siteEvent.damageNuisance !== false) {
                    summaryValue += "General nuisance.<br> ";
                }

                if (siteEvent.damageOther !== undefined && siteEvent.damageOther !== false) {
                    summaryValue += "Damaged other";
                    if (siteEvent.damageOtherSpec !== undefined && siteEvent.damageOtherSpec !== '')
                        summaryValue += ": " + siteEvent.damageOtherSpec;
                    else
                        summaryValue += ".";
                }
            }

            // Add the created cell data to the array
            cells.push(
                <TableCell>
                    {dayjs(siteEventDate).format("DD/MM/YYYY")}
                </TableCell>
            );

            // Add uncommon cells for: dog, deer, fox, feral cat, rabbit, myna, newpests, feral pig
            if (species === Species.WildDog || species === Species.Deer || species === Species.Fox || species === Species.FeralCat || 
                species === Species.Rabbit || species === Species.Myna || species === Species.NewPests || species === Species.FeralPig) {
                // Property name: for dog, fox, cat, pig, deer only
                if (species === Species.WildDog || species === Species.Fox || species === Species.FeralCat || 
                    species === Species.FeralPig || species === Species.Deer) {
                        cells.push(
                            <TableCell>
                                {propertyName}
                            </TableCell>
                        );
                }
                    

                // Site number: for all except myna, cat
                if (species !== Species.Myna && species !== Species.FeralCat){
                    cells.push(
                        <TableCell>
                            {siteNumber}
                        </TableCell>
                    );
                }
            }


            // Summary / Damage 
            if (species === Species.WildDog || species === Species.Deer || species === Species.Rabbit || 
                species === Species.FeralCat || species === Species.Fox || species === Species.FeralPig || 
                species === Species.OtherPests) {
                    cells.push(
                        <TableCell>
                            { parse(DOMPurify.sanitize(summaryValue)) }
                        </TableCell>
                    );
            }

            // Add the comment and edit column for all species.
            cells.push(
                <TableCell>
                    {notes}
                </TableCell>
            );
            cells.push(generateEditCell(siteEvent, user, theme));
        
            rows.push(cells);
        });

        return (
            <>
                {rows?.map((row, index) => (
                    <TableRow key={`damage-${index}`}>
                        {row.map((cell: any, index) => (
                            <Fragment key={`damage-${index}-cell`}>{cell}</Fragment>
                        ))}
                    </TableRow>
                ))}
            </>
        );
    };

    const getControlTableRows = (
        siteEvents: any[] | undefined, 
        lookups: Lookup[] | undefined, 
        species: Species, 
        user: User | null,
        theme: Theme
    ) => {
    let rows: JSX.Element[][] = [];
    let cells = []

    siteEvents?.map(siteEvent => {
        cells = [];
        var summaryValue = '';

        // Variable data for all species.
        var siteEventDate = undefined;

        if (siteEvent.startDateTime !== undefined)
            siteEventDate = siteEvent.startDateTime;

        var notes = siteEvent.comments !== undefined
            ? siteEvent.comments
            : '';

        // IACWEB-1111 - Only do for wild dogs, deer, fox, cat, rabbit, myna, newpests, pig and otherpests.
        if (species === Species.WildDog || species === Species.Deer || species === Species.Fox|| species === Species.FeralCat || 
            species === Species.Rabbit || species === Species.Myna || species === Species.NewPests || species === Species.FeralPig || 
            species === Species.OtherPests) {

            // Site number
            var siteNumber = siteEvent.siteNumber !== undefined
                ? siteEvent.siteNumber
                : '';

            // Property name: dog, fox, cat, pig, deer only
            if (species === Species.WildDog || species === Species.Fox || species === Species.FeralCat || 
                species === Species.FeralPig || species === Species.Deer) {
                var propertyName = siteEvent.propertyName !== undefined
                    ? siteEvent.propertyName
                    : '';
            }

            // Control technique for dog
            if (species === Species.WildDog) {
                //TechniqueAerialBaiting
                if (siteEvent.techniqueGroundBaiting !== undefined && siteEvent.techniqueGroundBaiting !== false && siteEvent.techniqueGroundBaiting !== '') {
                    summaryValue += "Ground Baiting: " + siteEvent.techniqueGroundBaiting + "<br> ";
                    if (siteEvent.techniqueGroundBaitingScore !== undefined) {
                        let lookupValue = lookups?.find(x => x.name === 'TechniqueGroundBaitingScore')?.items.find(x => x.id === siteEvent.techniqueGroundBaitingScore)?.value
                        summaryValue += lookupValue ? `Ground Baiting Score: ${lookupValue}<br>` : ''; 
                    }
                }
                //TechniqueJawTraps
                if (siteEvent.techniqueJawTraps !== undefined && siteEvent.techniqueJawTraps !== false && siteEvent.techniqueJawTraps !== '') {
                    summaryValue += "Jaw Traps: " + siteEvent.techniqueJawTraps + "<br> ";
                    if (siteEvent.techniqueJawTrapsScore !== undefined) {
                        let lookupValue = lookups?.find(x => x.name === 'TechniqueJawTrapsScore')?.items.find(x => x.id === siteEvent.techniqueJawTrapsScore)?.value
                        summaryValue += lookupValue ? `Jaw Traps Score: ${lookupValue}<br>` : ''; 
                    }
                }
                //TechniqueCageTraps
                if (siteEvent.techniqueCageTraps !== undefined && siteEvent.techniqueCageTraps !== false && siteEvent.techniqueCageTraps !== '') {
                    summaryValue += "Cage Traps: " + siteEvent.techniqueCageTraps + "<br> ";
                    if (siteEvent.techniqueCageTrapsScore !== undefined) {
                        let lookupValue = lookups?.find(x => x.name === 'TechniqueCageTrapsScore')?.items.find(x => x.id === siteEvent.techniqueCageTrapsScore)?.value
                        summaryValue += lookupValue ? `Cage Traps Score: ${lookupValue}<br>` : ''; 
                    }
                }
                //[TechniqueGroundShooting]
                if (siteEvent.techniqueGroundShooting !== undefined && siteEvent.techniqueGroundShooting !== false && siteEvent.techniqueGroundShooting !== '') {
                    summaryValue += "Ground Shooting: " + siteEvent.techniqueGroundShooting + "<br> ";
                    if (siteEvent.techniqueGroundShootingScore !== undefined) {
                        let lookupValue = lookups?.find(x => x.name === 'TechniqueGroundShootingScore')?.items.find(x => x.id === siteEvent.techniqueGroundShootingScore)?.value
                        summaryValue += lookupValue ? `Ground Shooting Score: ${lookupValue}<br>` : ''; 
                    }
                }
                //[TechniqueAerialShooting]
                if (siteEvent.techniqueAerialShooting !== undefined && siteEvent.techniqueAerialShooting !== false && siteEvent.techniqueAerialShooting !== '') {
                    summaryValue += "Aerial Shooting: " + siteEvent.techniqueAerialShooting + "<br> ";
                    if (siteEvent.techniqueAerialShootingScore !== undefined) {
                        let lookupValue = lookups?.find(x => x.name === 'TechniqueAerialShootingScore')?.items.find(x => x.id === siteEvent.techniqueAerialShootingScore)?.value
                        summaryValue += lookupValue ? `Aerial Shooting Score: ${lookupValue}<br>` : ''; 
                    }
                }
                //TechniqueLivestock
                if (siteEvent.techniqueLivestock !== undefined && siteEvent.techniqueLivestock !== false && siteEvent.techniqueLivestock !== '') {
                    summaryValue += "Livestock: " + siteEvent.techniqueLivestock + "<br> ";
                }
                //TechniqueExclusion
                if (siteEvent.techniqueExclusion !== undefined && siteEvent.techniqueExclusion !== false && siteEvent.techniqueExclusion !== '') {
                    summaryValue += "Exclusion: " + siteEvent.techniqueExclusion + "<br> ";
                }
                //TechniqueGroundBaitingPAPP
                if (siteEvent.techniqueGroundBaitingPAPP !== undefined && siteEvent.techniqueGroundBaitingPAPP !== false && siteEvent.techniqueGroundBaitingPAPP !== '') {
                    summaryValue += "Ground Baiting PAPP: " + siteEvent.techniqueGroundBaitingPAPP + "<br> ";
                    if (siteEvent.techniqueGroundBaitingPAPPScore !== undefined) {
                        let lookupValue = lookups?.find(x => x.name === 'TechniqueGroundBaitingPAPPScore')?.items.find(x => x.id === siteEvent.techniqueGroundBaitingPAPPScore)?.value
                        summaryValue += lookupValue ? `Ground Baiting PAPP Score: ${lookupValue}<br>` : ''; 
                    }
                }
                //TechniqueCanidPestEjector
                if (siteEvent.techniqueCanidPestEjector !== undefined && siteEvent.techniqueCanidPestEjector !== false && siteEvent.techniqueCanidPestEjector !== '') {
                    summaryValue += "Canid Pest Ejector: " + siteEvent.techniqueCanidPestEjector + "<br> ";
                    if (siteEvent.techniqueCanidPestEjectorScore !== undefined) {
                        let lookupValue = lookups?.find(x => x.name === 'TechniqueCanidPestEjectorScore')?.items.find(x => x.id === siteEvent.techniqueCanidPestEjectorScore)?.value
                        summaryValue += lookupValue ? `Canid Pest Ejector Score: ${lookupValue}<br>` : ''; 
                    }
                }
                //TechniqueStrychnineTraps
                if (siteEvent.techniqueStrychnineTraps !== undefined && siteEvent.techniqueStrychnineTraps !== false && siteEvent.techniqueStrychnineTraps !== '') {
                    summaryValue += "Strychnine Traps: " + siteEvent.techniqueStrychnineTraps + "<br> ";
                    if (siteEvent.techniqueStrychnineTrapsScore !== undefined) {
                        let lookupValue = lookups?.find(x => x.name === 'TechniqueStrychnineTrapsScore')?.items.find(x => x.id === siteEvent.techniqueStrychnineTrapsScore)?.value
                        summaryValue += lookupValue ? `Strychnine Traps: ${lookupValue}<br>` : ''; 
                    }
                }
                //LureType
                if (siteEvent.lureType !== undefined && siteEvent.lureType !== false && siteEvent.lureType !== '') {
                    summaryValue += "Lure Type: " + siteEvent.lureType + "<br> ";
                }
                //TechniqueCamera
                if (siteEvent.techniqueCamera !== undefined && siteEvent.techniqueCamera !== false && siteEvent.techniqueCamera !== '') {
                    summaryValue += "Cameras: " + siteEvent.techniqueCamera + "<br> ";
                }
                //TechniqueWarningSigns
                if (siteEvent.techniqueWarningSigns !== undefined && siteEvent.techniqueWarningSigns !== false && siteEvent.techniqueWarningSigns !== '') {
                    summaryValue += "Warning Signs: " + siteEvent.techniqueWarningSigns + "<br> ";
                }
                //TechniqueOther
                if (siteEvent.techniqueOther !== undefined && siteEvent.techniqueOther !== false && siteEvent.techniqueOther !== '') {
                    summaryValue += "Other: " + siteEvent.techniqueOther + "<br> ";
                    if (siteEvent.techniqueOtherSpec !== undefined && siteEvent.techniqueOtherSpec !== '')
                        summaryValue += "Other Spec: " + siteEvent.techniqueOtherSpec + "<br> ";
                }
            }

            // Control technique for deer
            if (species === Species.Deer) {
                if (siteEvent.techniqueNoiseDisturbance !== undefined && siteEvent.techniqueNoiseDisturbance !== false && siteEvent.techniqueNoiseDisturbance !== '') {
                    summaryValue += "Noise disturbance.<br> ";
                }

                if (siteEvent.techniqueFencing !== undefined && siteEvent.techniqueFencing !== false && siteEvent.techniqueFencing !== '') {
                    summaryValue += "Exclusion fencing.<br> ";
                }

                if (siteEvent.techniqueAerialShooting !== undefined && siteEvent.techniqueAerialShooting !== false && siteEvent.techniqueAerialShooting !== '') {
                    summaryValue += "Aerial shooting.<br> ";
                }

                if (siteEvent.techniqueGuardian !== undefined && siteEvent.techniqueGuardian !== false && siteEvent.techniqueGuardian !== '') {
                    summaryValue += "Livestock guardian animals.<br> ";
                }

                if (siteEvent.techniqueRepellents !== undefined && siteEvent.techniqueRepellents !== false && siteEvent.techniqueRepellents !== '') {
                    summaryValue += "Repellents.<br> ";
                }

                if (siteEvent.techniqueGroundShooting !== undefined && siteEvent.techniqueGroundShooting !== false && siteEvent.techniqueGroundShooting !== '') {
                    summaryValue += "Ground shooting: " + siteEvent.techniqueGroundShooting + "<br> ";
                }
                if (siteEvent.techniqueGroundShootingThermal !== undefined && siteEvent.techniqueGroundShootingThermal !== false && siteEvent.techniqueGroundShootingThermal !== '') {
                    summaryValue += "Ground shooting - thermal: " + siteEvent.techniqueGroundShootingThermal + "<br> ";
                }
                if (siteEvent.techniqueCommercialShooting !== undefined && siteEvent.techniqueCommercialShooting !== false && siteEvent.techniqueCommercialShooting !== '') {
                    summaryValue += "Commercial shooting: " + siteEvent.techniqueCommercialShooting + "<br> ";
                }

                if (siteEvent.techniqueTrapping !== undefined && siteEvent.techniqueTrapping !== false && siteEvent.techniqueTrapping !== '') {
                    summaryValue += "Trapping: " + siteEvent.techniqueTrapping + "<br> ";
                }

                if (siteEvent.techniqueOther !== undefined && siteEvent.techniqueOther !== false && siteEvent.techniqueOther !== '') {
                    summaryValue += "Other";
                    if (siteEvent.techniqueOtherSpec !== undefined && siteEvent.techniqueOtherSpec !== '')
                        summaryValue += ": " + siteEvent.techniqueOtherSpec + "<br>";
                    else
                        summaryValue += ".<br>";
                }
            }

            // Control technique for rabbit
            if (species === Species.Rabbit) {
                if (siteEvent.techniqueWarningSigns !== undefined && siteEvent.techniqueWarningSigns !== false && siteEvent.techniqueWarningSigns !== '') {
                    summaryValue += "Warning signs installed.<br> ";
                }

                if (siteEvent.techniqueRipping !== undefined && siteEvent.techniqueRipping !== false && siteEvent.techniqueRipping !== '') {
                    summaryValue += "Warren ripping or blasting";
                    if (siteEvent.techniqueRippingNumberDestroyed !== undefined && siteEvent.techniqueRippingNumberDestroyed !== '')
                        summaryValue += ": treated " + siteEvent.techniqueRippingNumberDestroyed + ".<br> ";
                    else
                        summaryValue += ".<br> ";
                }

                if (siteEvent.techniqueFumigation !== undefined && siteEvent.techniqueFumigation !== false && siteEvent.techniqueFumigation !== '') {
                    summaryValue += "Fumigation";
                    if (siteEvent.techniqueFumigationNumberFumigated !== undefined && siteEvent.techniqueFumigationNumberFumigated !== '')
                        summaryValue += ": treated " + siteEvent.techniqueFumigationNumberFumigated + ".<br> ";
                    else
                        summaryValue += ".<br> ";
                }

                if (siteEvent.techniqueFumigation !== undefined && siteEvent.techniqueFumigation !== false && siteEvent.techniqueFumigation !== '') {
                    summaryValue += "Fumigation";
                    if (siteEvent.techniqueFumigationNumberFumigated !== undefined && siteEvent.techniqueFumigationNumberFumigated !== '')
                        summaryValue += ": treated " + siteEvent.techniqueFumigationNumberFumigated + ".<br> ";
                    else
                        summaryValue += ".<br> ";
                }

                if (siteEvent.techniquePoison1080 !== undefined && siteEvent.techniquePoison1080 !== false && siteEvent.techniquePoison1080 !== '') {
                    summaryValue += "Poison baiting 1080: " + siteEvent.techniquePoison1080 + ".<br> ";
                }

                if (siteEvent.techniquePoisonPindone !== undefined && siteEvent.techniquePoisonPindone !== false && siteEvent.techniquePoisonPindone !== '') {
                    summaryValue += "Poison baiting Pindone: " + siteEvent.techniquePoisonPindone + ".<br> ";
                }

                if (siteEvent.techniqueShooting !== undefined && siteEvent.techniqueShooting !== false && siteEvent.techniqueShooting !== '') {
                    summaryValue += "Shooting";
                    if (siteEvent.techniqueShootingNumberShot !== undefined && siteEvent.techniqueShootingNumberShot !== '')
                        summaryValue += ": shot " + siteEvent.techniqueShootingNumberShot + ".<br> ";
                    else
                        summaryValue += ".<br> ";
                }

                if (siteEvent.techniqueTrapping !== undefined && siteEvent.techniqueTrapping !== false && siteEvent.techniqueTrapping !== '') {
                    summaryValue += "Trapping: " + siteEvent.techniqueTrapping + ".<br> ";
                }

                if (siteEvent.techniqueBioIntroRHDV !== undefined && siteEvent.techniqueBioIntroRHDV !== false && siteEvent.techniqueBioIntroRHDV !== '') {
                    summaryValue += "RHDV (active introduction).<br> ";
                }

                if (siteEvent.techniqueBioNatRHDV !== undefined && siteEvent.techniqueBioNatRHDV !== false && siteEvent.techniqueBioNatRHDV !== '') {
                    summaryValue += "RHDV.<br> ";
                }

                if (siteEvent.techniqueBioNatMyxo !== undefined && siteEvent.techniqueBioNatMyxo !== false && siteEvent.techniqueBioNatMyxo !== '') {
                    summaryValue += "Myxomatosis(natural spread).<br> ";
                }

                if (siteEvent.techniqueExclusion !== undefined && siteEvent.techniqueExclusion !== false && siteEvent.techniqueExclusion !== '') {
                    summaryValue += "Exclusion fencing.<br> ";
                }

                if (siteEvent.techniqueHarbourRemoval !== undefined && siteEvent.techniqueHarbourRemoval !== false && siteEvent.techniqueHarbourRemoval !== '') {
                    summaryValue += "Harbour removal (not ripping).<br> ";
                }

                if (siteEvent.techniqueOther !== undefined && siteEvent.techniqueOther !== false && siteEvent.techniqueOther !== '') {
                    summaryValue += "Other";
                    if (siteEvent.techniqueOtherSpec !== undefined && siteEvent.techniqueOtherSpec !== '')
                        summaryValue += ": " + siteEvent.techniqueOtherSpec + "<br>";
                    else
                        summaryValue += ".<br>";
                }
            }

            // Control technique for feral cat
            if (species === Species.FeralCat) {
                if (siteEvent.techniqueGroundShooting !== undefined && siteEvent.techniqueGroundShooting !== false && siteEvent.techniqueGroundShootingScore !== undefined) {
                    let lookupValue = lookups?.find(x => x.name === 'TechniqueGroundShootingScore')?.items.find(x => x.id === siteEvent.techniqueGroundShootingScore)?.value
                    summaryValue += lookupValue ? `Ground shooting (day), number shot: ${lookupValue}<br>` : ''; 
                }

                if (siteEvent.techniqueGroundShootingNight !== undefined && siteEvent.techniqueGroundShootingNight !== false && siteEvent.techniqueGroundShootingNightScore !== undefined) {
                    let lookupValue = lookups?.find(x => x.name === 'TechniqueGroundShootingNightScore')?.items.find(x => x.id === siteEvent.techniqueGroundShootingNightScore)?.value
                    summaryValue += lookupValue ? `Ground shooting (night), number shot: ${lookupValue}<br>` : ''; 
                }

                if (siteEvent.techniqueCageTraps !== undefined && siteEvent.techniqueCageTraps !== false && siteEvent.techniqueCageTrapsScore !== undefined) {
                    let lookupValue = lookups?.find(x => x.name === 'TechniqueCageTrapsScore')?.items.find(x => x.id === siteEvent.techniqueCageTrapsScore)?.value
                    summaryValue += lookupValue ? `Trapping using cage traps: ${lookupValue}<br>` : ''; 
                }

                if (siteEvent.techniqueJawTraps !== undefined && siteEvent.techniqueJawTraps !== false && siteEvent.techniqueJawTrapsScore !== undefined) {
                    let lookupValue = lookups?.find(x => x.name === 'TechniqueJawTrapsScore')?.items.find(x => x.id === siteEvent.techniqueJawTrapsScore)?.value
                    summaryValue += lookupValue ? `Trapping using padded-jaw traps: ${lookupValue}<br>` : ''; 
                }

                if (siteEvent.techniqueGuardAnimals !== undefined && siteEvent.techniqueGuardAnimals !== false && siteEvent.techniqueGuardAnimals !== '') {
                    summaryValue += "Guard animals (eg. domestic dogs, alpacas).<br> ";
                }

                if (siteEvent.techniqueExclusion !== undefined && siteEvent.techniqueExclusion !== false && siteEvent.techniqueExclusion !== '') {
                    summaryValue += "Exclusion fencing.<br> ";
                }

                if (siteEvent.techniqueOther !== undefined && siteEvent.techniqueOther !== false) {
                    summaryValue += "Other";
                    if (siteEvent.techniqueOtherSpec !== undefined)
                        summaryValue += ": " + siteEvent.techniqueOtherSpec + "<br>";
                    else
                        summaryValue += ".<br>";
                }
            }

            // Control technique for fox
            if (species === Species.Fox) {
                if (siteEvent.techniqueWarningSigns !== undefined && siteEvent.techniqueWarningSigns !== false && siteEvent.techniqueWarningSigns !== '') {
                    summaryValue += "Warning signs installed.<br> ";
                }

                if (siteEvent.techniqueGroundBaiting !== undefined && siteEvent.techniqueGroundBaiting !== false && siteEvent.techniqueGroundBaiting !== '') {
                    summaryValue += "Ground baiting: " + siteEvent.techniqueGroundBaiting + "<br> ";
                }

                if (siteEvent.techniqueJawTraps !== undefined && siteEvent.techniqueJawTraps !== false && siteEvent.techniqueJawTraps !== '') {
                    summaryValue += "Trapping using foot-hold traps: " + siteEvent.techniqueJawTraps + "<br>";
                }

                if (siteEvent.techniqueGroundShooting !== undefined && siteEvent.techniqueGroundShooting !== false && siteEvent.techniqueGroundShooting !== '') {
                    summaryValue += "Ground shooting , number shot: " + siteEvent.techniqueGroundShooting + "<br> ";
                }

                if (siteEvent.techniqueCanidPestEjector !== undefined && siteEvent.techniqueCanidPestEjector !== false && siteEvent.techniqueCanidPestEjector !== '') {
                    summaryValue += "Canid Pest Ejector: " + siteEvent.techniqueCanidPestEjector + "<br>";
                }

                if (siteEvent.techniqueExclusion !== undefined && siteEvent.techniqueExclusion !== false && siteEvent.techniqueExclusion !== '') {
                    summaryValue += "Exclusion fencing.<br> ";
                }

                if (siteEvent.techniqueGroundBaitingPapp !== undefined && siteEvent.techniqueGroundBaitingPapp !== false && siteEvent.techniqueGroundBaitingPapp !== '') {
                    summaryValue += "Ground baiting with PAPP: " + siteEvent.techniqueGroundBaitingPapp + "<br> ";
                }

                if (siteEvent.techniqueCageTraps !== undefined && siteEvent.techniqueCageTraps !== false && siteEvent.techniqueCageTraps !== '') {
                    summaryValue += "Trapping using cage traps: " + siteEvent.techniqueCageTraps + "<br>";
                }

                if (siteEvent.techniqueGuard !== undefined && siteEvent.techniqueGuard !== false && siteEvent.techniqueGuard !== '') {
                    summaryValue += "Livestock guardian animals.<br> ";
                }

                if (siteEvent.techniqueFumigation !== undefined && siteEvent.techniqueFumigation !== false && siteEvent.techniqueFumigation !== '') {
                    summaryValue += "Den fumigation.<br> ";
                }

                if (siteEvent.techniqueFoxDrives !== undefined && siteEvent.techniqueFoxDrives !== false && siteEvent.techniqueFoxDrives !== '') {
                    summaryValue += "Fox drives.<br> ";
                }

                if (siteEvent.techniqueAerialBaiting !== undefined && siteEvent.techniqueAerialBaiting !== false && siteEvent.techniqueAerialBaiting !== '') {
                    summaryValue += "Aerial baiting with 1080.<br> ";
                }

                if (siteEvent.techniqueOther !== undefined && siteEvent.techniqueOther !== false && siteEvent.techniqueOther !== '') {
                    summaryValue += "Other";
                    if (siteEvent.techniqueOtherSpec !== undefined && siteEvent.techniqueOtherSpec !== '')
                        summaryValue += ": " + siteEvent.techniqueOtherSpec + "<br>";
                    else
                        summaryValue += ".<br>";
                }
            }

            // Control technique for feral pig
            if (species === Species.FeralPig) {
                if (siteEvent.techniqueWarningSigns !== undefined && siteEvent.techniqueWarningSigns !== false && siteEvent.techniqueWarningSigns !== '') {
                    summaryValue += "Warning signs installed.<br> ";
                }

                if (siteEvent.techniqueCamera !== undefined && siteEvent.techniqueCamera !== false && siteEvent.techniqueCamera !== '') {
                    summaryValue += "Cameras installed.<br> ";
                }

                if (siteEvent.techniqueGroundBaiting !== undefined && siteEvent.techniqueGroundBaiting !== false && siteEvent.techniqueGroundBaiting !== '') {
                    summaryValue += "Ground baiting with 1080: " + siteEvent.techniqueGroundBaiting + "<br> ";
                }

                if (siteEvent.techniqueGroundBaitingHoggone !== undefined && siteEvent.techniqueGroundBaitingHoggone !== false && siteEvent.techniqueGroundBaitingHoggone !== '') {
                    summaryValue += "Ground baiting with Hoggone: " + siteEvent.techniqueGroundBaitingHoggone + "<br> ";
                }

                if (siteEvent.techniqueAerialBaiting !== undefined && siteEvent.techniqueAerialBaiting !== false && siteEvent.techniqueAerialBaiting !== '') {
                    summaryValue += "Aerial baiting with 1080.<br> ";
                }

                if (siteEvent.techniqueTrapping !== undefined && siteEvent.techniqueTrapping !== false && siteEvent.techniqueTrapping !== '') {
                    summaryValue += "Trapping (eg. cage traps): " + siteEvent.techniqueTrapping + "<br>";
                }

                if (siteEvent.techniqueGroundShooting !== undefined && siteEvent.techniqueGroundShooting !== false && siteEvent.techniqueGroundShooting !== '') {
                    summaryValue += "Ground shooting , number shot: " + siteEvent.techniqueGroundShooting + "<br> ";
                }

                if (siteEvent.techniqueGroundShootingThermal !== undefined && siteEvent.techniqueGroundShootingThermal !== false && siteEvent.techniqueGroundShootingThermal !== '') {
                    summaryValue += "Ground shooting thermal , number shot: " + siteEvent.techniqueGroundShootingThermal + "<br> ";
                }

                if (siteEvent.techniqueAerialShooting !== undefined && siteEvent.techniqueAerialShooting !== false && siteEvent.techniqueAerialShooting !== '') {
                    summaryValue += "Aerial shooting.<br>";
                }

                if (siteEvent.techniqueCommercialShooting !== undefined && siteEvent.techniqueCommercialShooting !== false && siteEvent.techniqueCommercialShooting !== '') {
                    summaryValue += "Shooting for commercial use.<br> ";
                }

                if (siteEvent.techniqueFencing !== undefined && siteEvent.techniqueFencing !== false && siteEvent.techniqueFencing !== '') {
                    summaryValue += "Exclusion fencing.<br> ";
                }

                if (siteEvent.techniqueJudasPig !== undefined && siteEvent.techniqueJudasPig !== false && siteEvent.techniqueJudasPig !== '') {
                    summaryValue += "Use of Judas pigs.<br>";
                }

                if (siteEvent.techniqueOther !== undefined && siteEvent.techniqueOther !== false && siteEvent.techniqueOther !== '') {
                    summaryValue += "Other";
                    if (siteEvent.techniqueOtherSpec !== undefined && siteEvent.techniqueOtherSpec !== '')
                        summaryValue += ": " + siteEvent.techniqueOtherSpec + "<br>";
                    else
                        summaryValue += ".<br>";
                }
            }

            // Control technique for other pests
            if (species === Species.OtherPests) {
                if (siteEvent.techniqueMustering !== undefined && siteEvent.techniqueWarningSigns !== false && siteEvent.techniqueMustering !== '') {
                    summaryValue += "Mustering.<br> ";
                }

                if (siteEvent.techniqueGroundShootingToLeave !== undefined && siteEvent.techniqueGroundBaiting !== false && siteEvent.techniqueGroundShootingToLeave !== '') {
                    summaryValue += "Ground shooting - to leave.<br> ";
                }

                if (siteEvent.techniqueGroundShootingToUse !== undefined && siteEvent.techniqueAerialBaiting !== false && siteEvent.techniqueGroundShootingToUse !== '') {
                    summaryValue += "Ground shooting - to use.<br> ";
                }

                if (siteEvent.techniqueAerialShooting !== undefined && siteEvent.techniqueTrapping !== false && siteEvent.techniqueAerialShooting !== '') {
                    summaryValue += "Aerial shooting.<br>";
                }

                if (siteEvent.techniqueExclusionFencing !== undefined && siteEvent.techniqueGroundShooting !== false && siteEvent.techniqueExclusionFencing !== '') {
                    summaryValue += "Exclusion fencing to prevent damage.<br> ";
                }

                if (siteEvent.techniqueOther !== undefined && siteEvent.techniqueOther !== false && siteEvent.techniqueOther !== '') {
                    summaryValue += "Other";
                    if (siteEvent.techniqueOtherSpec !== undefined && siteEvent.techniqueOtherSpec !== '')
                        summaryValue += ": " + siteEvent.techniqueOtherSpec + "<br>";
                    else
                        summaryValue += ".<br>";
                }
            }

        }

        // Set up the elements for common cells of all species.
        cells.push(
            <TableCell>
                {dayjs(siteEventDate).format("DD/MM/YYYY")}
            </TableCell>
        );

        // Add uncommon cells for: dog, deer, fox, cat, rabbit, myna, newpests
        if (species === Species.WildDog || species === Species.Deer || species === Species.Fox || 
            species === Species.FeralCat || species === Species.Rabbit || species === Species.Myna || 
            species === Species.NewPests || species === Species.FeralPig || Species.OtherPests) {
            // Add cells that are special to dog, fox, cat, pig, deer only
            if (species === Species.WildDog || species === Species.Fox || species === Species.FeralCat || 
                species === Species.FeralPig || species === Species.Deer) {
                    cells.push(
                        <TableCell>
                            {propertyName}
                        </TableCell>
                    );
            }

            cells.push(
                <TableCell>
                    {siteNumber}
                </TableCell>
            );
        }

        // Control technique cell for dog, deer, rabbit, cat, pig, fox, otherpests
        if (species === Species.WildDog || species === Species.Deer || species === Species.Rabbit || 
            species === Species.FeralCat || species === Species.Fox || species === Species.FeralPig || 
            species === Species.OtherPests) {
                cells.push(
                    <TableCell>
                        { parse(DOMPurify.sanitize(summaryValue)) }
                    </TableCell>
                );
            }

        // Add the comments and edit column for all species.
        cells.push(
            <TableCell>
                {notes}
            </TableCell>
        );

        cells.push(generateEditCell(siteEvent, user, theme));
    
        rows.push(cells);
    });
    return (
        <>
            {rows?.map((row, index) => (
                <TableRow key={`control-${index}`}>
                    {row.map((cell: any, index) => (
                        <Fragment key={`control-${index}-cell`}>{cell}</Fragment>
                    ))}
                </TableRow>
            ))}
        </>
    );
    };

    const getDiseaseTableRows = (
        siteEvents: any[] | undefined, 
        species: Species, 
        user: User | null,
        theme: Theme
    ) => {
    let rows: JSX.Element[][] = [];
    let cells = []

    siteEvents?.map(siteEvent => {
        cells = [];

        // Variable data for all species.
        var siteEventDate = undefined;

        if (siteEvent.startDateTime !== undefined)
            siteEventDate = siteEvent.startDateTime;

        var notes = siteEvent.comments !== undefined
            ? siteEvent.comments
            : '';

        // Site number: rabbit only
        if (species === Species.Rabbit) {
            var siteNumber = siteEvent.siteNumber !== undefined
                ? siteEvent.siteNumber
                : '';
        }

        // Set up the elements for common cells of all species.
        cells.push(
            <TableCell>
                {dayjs(siteEventDate).format("DD/MM/YYYY")}
            </TableCell>
        );

        // Site number: rabbit only
        if (species === Species.Rabbit) {
            cells.push(
                <TableCell>
                    {siteNumber}
                </TableCell>
            );

        }

        cells.push(
            <TableCell>
                {notes}
            </TableCell>
        );
        
        cells.push(generateEditCell(siteEvent, user, theme));
    
        rows.push(cells);
    });
    return (
        <>
            {rows?.map((row, index) => (
                <TableRow key={`disease-${index}`}>
                    {row.map((cell: any, index) => (
                        <Fragment key={`disease-${index}-cell`}>{cell}</Fragment>
                    ))}
                </TableRow>
            ))}
        </>
    );
};

    // Generate table data from site events response
    const tableRows = useMemo(() => {
        if (!siteEventsLoading && siteEvents && siteEvents.siteEvents?.length !== 0) {
            if (siteEventType === SiteEventType.Sighting){
                return getSightingTableRows(siteEvents.siteEvents, siteEvents.lookups, species, user, theme);
            }
            else if (siteEventType === SiteEventType.Damage){
                return getDamageTableRows(siteEvents.siteEvents, species, user, theme);
            }
            else if (siteEventType === SiteEventType.Control){
                return getControlTableRows(siteEvents.siteEvents, siteEvents.lookups, species, user, theme);
            }
            else if (siteEventType === SiteEventType.Disease){
                return getDiseaseTableRows(siteEvents.siteEvents, species, user, theme);
            }
            return (
                <></>
            );
        }
        return <></>

    }, [siteEvents, species, user, speciesName, navigate, theme]);

    const handleChangePage = (
        _: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage);
    };
    
    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Card variant='outlined'>
            <CardContent>
                <Grid container spacing={1} alignItems={"center"}>
                    <Grid item xs={6}>
                        <Box display="flex" alignItems={"center"}>
                            <FontAwesomeIcon icon={faTable} style={{ fontSize: '1.2rem', paddingRight: "5px" }} />
                            <Typography paddingLeft={"5px"} fontSize={24}>
                                {title}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box display="flex" justifyContent={"end"}>
                            <TablePagination
                                component="div"
                                count={totalRecords}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{padding: "10px"}} />

                <Table>
                    <TableHead>
                        <TableRow sx={{backgroundColor: "#f0f0f0"}}>
                            <>
                                {tableHeaders.map((header, index) => (
                                    <TableCell align="left" key={`header-${index}`}>
                                        <Typography fontSize={16} fontWeight={600}>
                                            {header}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!siteEventsLoading && siteEvents && siteEvents.siteEvents?.length !== 0 && (
                            <>
                                {tableRows}
                            </>
                        )}
                    </TableBody>
                </Table>
                { siteEventsLoading && (
                    <Box sx={{display: "flex", justifyContent: "center", width: "auto", paddingTop: "20px"}}>
                        <CircularProgress />
                    </Box>
                )}
                {(!siteEventsLoading && siteEvents && siteEvents.siteEvents?.length === 0) && (
                    <Typography sx={{padding: "20px"}}>
                        No records were found that match the current filters.
                    </Typography>
                )}
            </CardContent>
        </Card>
    )
}

export default RecordsCard